// components/steps/StepInterests.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Default generic interests
const genericInterests = [
  'Hiking', 'Museums', 'Nightlife', 'Shopping',
  'History', 'Food', 'Art', 'Beaches',
  'Adventure', 'Relaxation'
];

const StepInterests = ({ data, updateData, skip, goBack }) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState('');
  const [viewAll, setViewAll] = useState(false);

  // Initialize options + selected when coming back to this step
  useEffect(() => {
    const interests = data.interests || [];
    setSelected(interests);

    // Extract any custom ones not in generic
    const customInterests = interests.filter(i =>
      !genericInterests.some(g => g.toLowerCase() === i.toLowerCase())
    );
    setOptions([...customInterests, ...genericInterests]);
  }, [data.interests]);

  const toggleInterest = (interest) => {
    setSelected(prev =>
      prev.includes(interest)
        ? prev.filter(i => i !== interest)
        : [...prev, interest]
    );
  };

  // Add a custom interest at the top
  const addCustom = () => {
    const value = custom.trim();
    if (!value) return;
    setOptions(prev => [value, ...prev.filter(i => i.toLowerCase() !== value.toLowerCase())]);
    if (!selected.includes(value)) setSelected(prev => [...prev, value]);
    setCustom('');
    setViewAll(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustom();
    }
  };

  // Which to show
  const displayed = viewAll ? options : options.slice(0, 6);
  const isNextDisabled = selected.length === 0;

  return (
    <motion.div
      layout
      className="bg-white/60 backdrop-blur p-6 rounded-xl shadow-lg max-h-[80vh] overflow-y-auto"
    >
      <h2 className="text-xl font-semibold mb-4">What are you interested in?</h2>

      {/* Animated grid */}
      <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-2">
        {displayed.map(interest => (
          <motion.button
            layout
            key={interest}
            onClick={() => toggleInterest(interest)}
            className={`px-4 py-2 rounded-lg border transition-colors whitespace-nowrap text-sm
              ${selected.includes(interest)
                ? 'bg-blue-500 text-white border-transparent'
                : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
          >
            {interest}
          </motion.button>
        ))}
      </motion.div>

      {/* View more / less */}
      {options.length > 6 && (
        <button
          onClick={() => setViewAll(v => !v)}
          className="text-xs text-blue-600 hover:underline mb-4 transition"
        >
          {viewAll ? 'View less ▲' : 'View more ▼'}
        </button>
      )}

      {/* Custom interest input */}
      <div className="flex mb-6">
        <input
          type="text"
          className="flex-1 p-3 border rounded-l-lg focus:outline-none"
          placeholder="Add your own interest"
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          onClick={addCustom}
          className="px-4 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600 transition"
        >
          Add
        </button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        {goBack && (
          <button
            onClick={goBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            Back
          </button>
        )}
        <div className="flex space-x-4">
          <button
            onClick={() => updateData('interests', selected)}
            disabled={isNextDisabled}
            className={`px-6 py-2 rounded-lg transition 
              ${isNextDisabled
                ? 'bg-blue-300 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            Next
          </button>
          <button
            onClick={skip}
            className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
          >
            Skip
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default StepInterests;
